import React, { useState } from "react";
import { ShieldCheck, AlertTriangle, MailCheck, Lock, MessageSquare, BadgeCheck } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import ClaimNumberDialog from "@/components/ClaimNumberDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const CATEGORY_TONE = {
  "No verified disputes currently visible": "text-emerald-700 bg-emerald-50 border-emerald-200",
  "Limited public information": "text-slate-700 bg-slate-50 border-slate-200",
  "Some disputes recorded": "text-amber-700 bg-amber-50 border-amber-200",
  "Multiple unresolved disputes": "text-orange-700 bg-orange-50 border-orange-200",
  "Profile requires caution": "text-rose-700 bg-rose-50 border-rose-200",
  "Information under review": "text-yellow-700 bg-yellow-50 border-yellow-200",
};

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-2xl font-display font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

export default function TransparencyProfile({ profile }) {
  const { user, openAuth } = useAuth();
  const [claimOpen, setClaimOpen] = useState(false);
  const tone = CATEGORY_TONE[profile.category_label] || "text-slate-700 bg-slate-50 border-slate-200";

  return (
    <div className="space-y-5" data-testid="transparency-profile">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Dispute Transparency Profile</p>
          <h3 className="font-display text-2xl font-bold text-slate-900 mt-1">
            {profile.is_claimed ? (profile.display_name || "Verified party") : "Unregistered number"}
          </h3>
          <p className="font-mono text-sm text-slate-500">{profile.masked_phone}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {profile.identity_verified ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-brand/10 text-teal px-3 py-1 text-xs font-semibold" data-testid="verified-number-badge">
              <BadgeCheck className="h-4 w-4" /> Verified number
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-600 px-3 py-1 text-xs font-semibold">
              Number not verified
            </span>
          )}
          {profile.email_verified && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold" data-testid="verified-email-badge">
              <MailCheck className="h-4 w-4" /> Verified email
            </span>
          )}
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${tone}`}>
        <p className="font-semibold">{profile.category_label}</p>
      </div>

      {profile.public_statement && (
        <div className="rounded-xl border border-teal-brand/30 bg-teal-brand/5 p-4" data-testid="party-statement">
          <p className="text-xs font-semibold text-teal flex items-center gap-1.5 mb-1">
            <MessageSquare className="h-4 w-4" /> Response from the listed party
          </p>
          <p className="text-sm text-slate-700">{profile.public_statement}</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="Complaints recorded" value={profile.total_complaints} />
        <Metric label="Response rate" value={`${profile.response_rate}%`} />
        <Metric label="Resolved by agreement" value={profile.resolved} />
        <Metric label="Unresolved" value={profile.unresolved} />
      </div>

      {profile.records_require_login ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center" data-testid="records-login-gate">
          <Lock className="h-6 w-6 text-slate-400 mx-auto" />
          <p className="text-sm font-semibold text-slate-700 mt-2">
            {profile.records_count > 0
              ? `${profile.records_count} dispute record${profile.records_count > 1 ? "s" : ""} available`
              : "No visible dispute records"}
          </p>
          <p className="text-xs text-slate-500 mt-1">Log in to view the individual dispute records for this number.</p>
          {profile.records_count > 0 && (
            <Button className="mt-3 bg-teal-brand hover:bg-teal-700 text-white" onClick={() => openAuth("login")} data-testid="records-login-btn">
              Log in to view records
            </Button>
          )}
        </div>
      ) : (
        profile.disputes?.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-slate-700">Visible dispute records</p>
            {profile.disputes.map((d) => (
              <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`dispute-record-${d.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{d.primary_category || d.categories?.[0] || "Dispute"}</p>
                    <p className="text-xs text-slate-500">{d.complaint_code} · {d.date_range}{d.city ? ` · ${d.city}` : ""}</p>
                  </div>
                  <StatusBadge status={d.status} />
                </div>
                {d.has_response && (
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                      <MessageSquare className="h-3.5 w-3.5" /> This party has submitted a response
                      {d.response_type && (
                        <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] ${d.response_type === "agree" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {d.response_type === "agree" ? "Agreed" : "Disputed"}
                        </span>
                      )}
                    </p>
                    {d.public_reply && <p className="text-xs text-slate-600 mt-1">{d.public_reply}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <ShieldCheck className="h-4 w-4 text-teal" /> Is this your number?
        </div>
        <Button variant="outline" onClick={() => { if (!user) { openAuth("login"); return; } setClaimOpen(true); }} data-testid="claim-number-cta">
          <BadgeCheck className="h-4 w-4 mr-2" /> Claim &amp; respond
        </Button>
      </div>

      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 leading-relaxed">
          This information may be incomplete and does not establish guilt. It is not a criminal record, credit report,
          legal judgment, or guarantee of future conduct.
        </p>
      </div>

      <ClaimNumberDialog open={claimOpen} onOpenChange={setClaimOpen} initialPhone={profile._searched_phone || ""} />
    </div>
  );
}
